import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import successIcon from '../../../../global/media/success-green-icon.svg';
import { OrderTableData } from '../../../OrdersPage/types';
import { getFromLocalStorage } from '../../../../global/helpers';
import * as actionsOrder from '../../actions';
import * as actionsOrders from '../../../OrdersPage/actions';
import * as actionsNotification from '../../../InitComponent/actions';
import { selectors } from '../../reducer';
import { selectors as selectorsLogin } from '../../../LoginPage/reducer';
import { WOLT_CANCEL_REASONS, WOLT_CANCEL_REQUESTED_STATUS } from './constants';
import {
  buildWoltCancelPayload,
  canRequestWoltCancel,
  extractWoltCode,
  formatWoltCancelSentAt,
  getWoltCancelSentAt,
  saveWoltCancelSentAt,
} from './helpers';

interface WoltCancelButtonProps {
  data: OrderTableData;
}

const WoltCancelButton = ({ data }: WoltCancelButtonProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const role = getFromLocalStorage('role');
  const token = getFromLocalStorage('token');
  const woltCancel = useSelector(selectors.woltCancel);
  const dataUser = useSelector(selectorsLogin.userData);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [reason, setReason] = useState<string>('');
  const [sentAt, setSentAt] = useState<string>(() =>
    getWoltCancelSentAt(data.id)
  );

  if (!canRequestWoltCancel(role, data)) return null;

  // Статус заказа переживает перезагрузку, отметка времени — только локально
  const isSent =
    Boolean(sentAt) || data.statusPharmacy === WOLT_CANCEL_REQUESTED_STATUS;

  const woltCode = extractWoltCode(data.comment);
  const pharmacyName = data.sourceName ?? '';
  const pharmacyAddress = data.sourceAddress ?? '';
  // Номер фармацевта — строкой, чтобы не потерять ведущие нули
  const pharmacistNumber = String(dataUser?.phone ?? '');

  const notifyError = (content: string) =>
    dispatch(
      actionsNotification.addApplicationNotification({
        content,
        canBeClosed: true,
        type: 'error',
      })
    );

  // Валидируем перед открытием: без кода/данных аптеки отправлять нечего
  const handleOpen = () => {
    if (!woltCode) {
      notifyError(t('OrderPage.WoltCancel.NO_CODE'));
      return;
    }
    if (!pharmacyName || !pharmacyAddress) {
      notifyError(t('OrderPage.WoltCancel.NO_PHARMACY_DATA'));
      return;
    }
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setReason('');
  };

  const handleConfirm = () => {
    if (!reason) {
      notifyError(t('OrderPage.WoltCancel.NO_REASON'));
      return;
    }
    dispatch(
      actionsOrder.sendWoltCancel.request({
        data: buildWoltCancelPayload(data, pharmacistNumber, reason),
        token,
        postEffect: () => {
          const now = new Date().toISOString();
          saveWoltCancelSentAt(data.id, now);
          setSentAt(now);
          setIsOpen(false);
          setReason('');
          dispatch(
            actionsOrders.setSelectedOrder.request({
              role,
              id: data.id,
              testOrders: data.test,
            })
          );
        },
      })
    );
  };

  if (isSent) {
    return (
      <Box
        sx={{
          m: 2,
          mt: 0,
          width: 'calc(100% - 32px)',
          maxWidth: '280px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: '#e8f5e9',
          border: '2px solid #a5d6a7',
          color: '#1e8e3e',
          fontSize: 14,
          fontWeight: 700,
          lineHeight: 1.3,
          textAlign: 'center',
        }}
      >
        <img src={successIcon} width={24} height={24} alt="" />
        <span>
          {sentAt
            ? t('OrderPage.WoltCancel.SENT_AT', {
                time: formatWoltCancelSentAt(sentAt),
              })
            : t('OrderPage.WoltCancel.SENT')}
        </span>
      </Box>
    );
  }

  return (
    <>
      <Button
        variant="outlined"
        color="error"
        sx={{
          m: 2,
          width: 'calc(100% - 32px)',
          mt: 0,
          border: '3px solid',
          fontWeight: 900,
        }}
        onClick={handleOpen}
      >
        {t('OrderPage.WoltCancel.BUTTON')}
      </Button>

      <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>{t('OrderPage.WoltCancel.TITLE')}</DialogTitle>
        <DialogContent>
          <p>{t('OrderPage.WoltCancel.DESCRIPTION')}</p>
          <p>
            {t('OrderPage.WoltCancel.ORDER')}: <b>{data.id}</b>
          </p>
          <p>
            {t('OrderPage.WoltCancel.WOLT_CODE')}: <b>{woltCode}</b>
          </p>
          <p>
            {t('OrderPage.WoltCancel.PHARMACY')}: <b>{pharmacyName}</b>
          </p>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="wolt-cancel-reason-label">
              {t('OrderPage.WoltCancel.REASON')}
            </InputLabel>
            <Select
              labelId="wolt-cancel-reason-label"
              value={reason}
              label={t('OrderPage.WoltCancel.REASON')}
              onChange={(event) => setReason(event.target.value)}
            >
              {WOLT_CANCEL_REASONS.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {t(`OrderPage.WoltCancel.Reasons.${item.labelKey}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={woltCancel.loading}>
            {t('OrderPage.WoltCancel.CANCEL')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirm}
            disabled={woltCancel.loading || !reason}
          >
            {t('OrderPage.WoltCancel.CONFIRM')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WoltCancelButton;
